import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, Shield, Heart, X, Sparkles, Star, AlertTriangle, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import api from '@/api/client'
import { useSocket } from '@/hooks/useSocket'
import { toast } from '@/components/ui/toaster'
import { formatDistanceToNow } from '@/lib/utils'

interface EphemeralMessage {
  _id: string
  sender: string
  senderRole: 'user' | 'expert' | 'peer_supporter' | 'ai_chatbot' | 'unassigned'
  content: string
  createdAt: string
}

interface EphemeralChat {
  _id: string
  user: string
  responder: string | null
  responderType: 'expert' | 'peer_supporter' | 'ai_chatbot' | 'unassigned'
  status: string
  triggerType: string
  messages: EphemeralMessage[]
  createdAt: string
  expiresAt: string
  lastMessage?: EphemeralMessage | null
}

export default function EphemeralSupport() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [chat, setChat] = useState<EphemeralChat | null>(null)
  const [chats, setChats] = useState<EphemeralChat[]>([])
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [resolved, setResolved] = useState(false)
  const [rating, setRating] = useState<number | null>(null)
  const [typing, setTyping] = useState(false)
  const [showWarning, setShowWarning] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const socket = useSocket()

  useEffect(() => {
    if (id) {
      loadChat()
    } else {
      loadChats()
    }
    // Start polling for the chat list so new/active chats appear without manual refresh
    const interval = setInterval(() => {
      if (id) {
        loadChat()
      } else {
        loadChats()
      }
    }, 5000)
    return () => clearInterval(interval)
  }, [id])

  const loadChats = async () => {
    try {
      const r = await api.get('/ephemeral-chats')
      const list = r.data || []
      setChats(list)
      // If exactly one active/waiting chat exists, auto-open it for seamless support
      const active = list.filter((c: EphemeralChat) => c.status !== 'resolved' && c.status !== 'expired')
      if (active.length === 1) {
        navigate(`/ephemeral-support/${active[0]._id}`, { replace: true })
        return
      }
    } catch (err: any) {
      toast({ title: t('common.error'), variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (chat && socket) {
      socket.emit('ephemeral:join', { chatId: chat._id })
      const handleNewMessage = (data: { chatId: string; message: EphemeralMessage }) => {
        if (data.chatId === chat._id) {
          setChat(prev => prev ? { ...prev, messages: [...prev.messages, data.message] } : prev)
        }
      }
      const handleTyping = (data: { userId: string; username: string; isTyping: boolean }) => {
        if (data.userId !== chat.user && data.isTyping) {
          setTyping(true)
          setTimeout(() => setTyping(false), 3000)
        }
      }
      const handleResolved = (data: { chatId: string }) => {
        if (data.chatId === chat._id) setResolved(true)
      }

      socket.on('ephemeral:message', handleNewMessage)
      socket.on('ephemeral:typing', handleTyping)
      socket.on('ephemeral:resolved', handleResolved)

      return () => {
        socket.emit('ephemeral:leave', { chatId: chat._id })
        socket.off('ephemeral:message', handleNewMessage)
        socket.off('ephemeral:typing', handleTyping)
        socket.off('ephemeral:resolved', handleResolved)
      }
    }
  }, [chat?._id, socket])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chat?.messages])

  const loadChat = async () => {
    if (!id) return
    try {
      const r = await api.get(`/ephemeral-chats/${id}`)
      setChat(r.data)
    } catch (err: any) {
      if (err.response?.status === 410) {
        toast({ title: t('ephemeral.warningTitle'), description: 'Sohbet süresi dolmuş', variant: 'destructive' })
        navigate('/feed')
      } else {
        toast({ title: t('common.error'), variant: 'destructive' })
      }
    } finally {
      setLoading(false)
    }
  }

  const sendMessage = async () => {
    if (!chat || !message.trim() || sending || resolved) return
    setSending(true)
    try {
      await api.post(`/ephemeral-chats/${chat._id}/messages`, { content: message.trim() })
      setMessage('')
    } catch (err: any) {
      toast({ title: t('common.error'), variant: 'destructive' })
    } finally {
      setSending(false)
    }
  }

  const handleResolve = async (rate: number) => {
    if (!chat) return
    try {
      await api.post(`/ephemeral-chats/${chat._id}/resolve`, { rating: rate })
      setRating(rate)
      setResolved(true)
      toast({ title: t('ephemeral.rateThanks') })
    } catch (err) {
      // ignore
    }
  }

  const handleTypingEvent = () => {
    if (chat && socket) {
      socket.emit('ephemeral:typing', { chatId: chat._id, isTyping: true })
    }
  }

  const expiresIn = chat ? (() => {
    const ms = new Date(chat.expiresAt).getTime() - Date.now()
    const hours = Math.floor(ms / 3600000)
    const minutes = Math.floor((ms % 3600000) / 60000)
    return { hours, minutes }
  })() : null

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="animate-pulse space-y-3">
          <div className="h-32 w-96 bg-secondary rounded-2xl" />
        </div>
      </div>
    )
  }

  if (!chat) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <Shield className="w-12 h-12 text-primary mx-auto mb-3" />
            <h2 className="font-display text-xl font-bold mb-2">{t('ephemeral.title')}</h2>
            <p className="text-sm text-muted-foreground">{t('ephemeral.listSubtitle')}</p>
          </div>

          {chats.length === 0 ? (
            <div className="p-6 rounded-2xl bg-card border border-border text-center">
              <p className="text-muted-foreground mb-4">{t('ephemeral.noActiveChats')}</p>
              <Button onClick={() => navigate('/feed')}>{t('common.back')}</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {chats.map((c) => (
                <button
                  key={c._id}
                  onClick={() => navigate(`/ephemeral-support/${c._id}`)}
                  className="w-full p-4 rounded-2xl bg-card border border-border hover:border-primary/50 transition-colors text-left"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">
                      {c.responderType === 'ai_chatbot' ? t('ephemeral.aiMatched') : c.responderType === 'expert' ? t('ephemeral.expertMatched') : t('ephemeral.peerMatched')}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      c.status === 'active' ? 'bg-green-500/10 text-green-500' :
                      c.status === 'waiting_match' ? 'bg-yellow-500/10 text-yellow-500' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {c.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {c.lastMessage?.content || t('ephemeral.startChat')}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    {formatDistanceToNow(c.createdAt)}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  const isAI = chat.responderType === 'ai_chatbot'

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 bg-gradient-to-b from-primary/5 to-transparent">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-2xl h-[80vh] flex flex-col rounded-2xl bg-card border border-border shadow-xl overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-border bg-secondary/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isAI ? 'bg-primary/20' : 'bg-green-500/20'}`}>
              {isAI ? <Sparkles className="w-5 h-5 text-primary" /> : <Heart className="w-5 h-5 text-green-500" />}
            </div>
            <div>
              <h2 className="font-semibold text-sm">{t('ephemeral.title')}</h2>
              <p className="text-xs text-muted-foreground">
                {isAI ? t('ephemeral.aiMatched') : chat.responderType === 'expert' ? t('ephemeral.expertMatched') : t('ephemeral.peerMatched')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/feed')}
            className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
            aria-label={t('ephemeral.exit')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Privacy banner */}
        <div className="px-4 py-2 bg-primary/5 border-b border-border flex items-center gap-2 text-xs text-muted-foreground">
          <Shield className="w-3.5 h-3.5 text-primary flex-shrink-0" />
          <span>{t('ephemeral.autoDelete')}</span>
          {expiresIn && (
            <span className="ml-auto flex items-center gap-1 text-orange-500">
              <Clock className="w-3 h-3" />
              {t('ephemeral.expiresIn', { hours: expiresIn.hours, minutes: expiresIn.minutes })}
            </span>
          )}
        </div>

        {/* Initial warning */}
        <AnimatePresence>
          {showWarning && !resolved && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="px-4 py-3 bg-amber-500/5 border-b border-amber-500/20"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">{t('ephemeral.warningTitle')}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t('ephemeral.warningBody')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowWarning(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {chat.messages.map((msg, i) => {
            const isMine = msg.senderRole === 'user'
            const isBot = msg.senderRole === 'ai_chatbot'
            return (
              <motion.div
                key={msg._id || i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${isMine
                  ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-md'
                  : isBot
                    ? 'bg-primary/10 border border-primary/20 rounded-2xl rounded-bl-md'
                    : 'bg-secondary rounded-2xl rounded-bl-md'
                } px-4 py-2.5`}>
                  {!isMine && (
                    <div className="flex items-center gap-1.5 mb-1">
                      {isBot ? <Sparkles className="w-3 h-3 text-primary" /> : <Heart className="w-3 h-3 text-green-500" />}
                      <span className="text-[10px] font-medium text-muted-foreground">
                        {isBot ? 'Destek Botu' : 'Destek'}
                      </span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  <p className={`text-[10px] mt-1 ${isMine ? 'opacity-70' : 'text-muted-foreground'}`}>
                    {formatDistanceToNow(msg.createdAt)}
                  </p>
                </div>
              </motion.div>
            )
          })}

          {typing && (
            <div className="flex justify-start">
              <div className="bg-secondary/50 rounded-2xl rounded-bl-md px-4 py-2.5 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '300ms' }} />
                <span className="text-xs text-muted-foreground ml-2">{t('ephemeral.aiTyping')}</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input or Resolved state */}
        {!resolved ? (
          <div className="p-4 border-t border-border bg-secondary/30">
            <div className="flex gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value)
                  handleTypingEvent()
                }}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={t('ephemeral.placeholder')}
                maxLength={2000}
                disabled={sending}
                className="flex-1 px-4 py-2.5 rounded-full bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
              />
              <Button
                onClick={sendMessage}
                disabled={!message.trim() || sending}
                size="icon"
                className="rounded-full"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <button
              type="button"
              onClick={() => handleResolve(0)}
              className="text-xs text-muted-foreground hover:text-foreground mt-2 mx-auto block"
            >
              {t('ephemeral.exit')}
            </button>
          </div>
        ) : (
          <div className="p-4 border-t border-border bg-secondary/30 text-center">
            <p className="text-sm font-medium mb-2">Bu sohbet tamamlandı</p>
            {rating === null && (
              <>
                <p className="text-xs text-muted-foreground mb-3">{t('ephemeral.rate')}</p>
                <div className="flex justify-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleResolve(star)}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star className={`w-6 h-6 ${rating && rating >= star ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`} />
                    </button>
                  ))}
                </div>
              </>
            )}
            <Button variant="secondary" className="mt-4" onClick={() => navigate('/feed')}>
              {t('common.close')}
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  )
}
